// -----------------------------------------------------------------------------
// core/history.ts — the undo/redo stacks.
//
// Two stacks of Commands. `push` records a freshly-executed command and clears
// the redo stack (a new edit invalidates any redo future). `undo` moves the top
// command to the redo stack and hands it back so the store can call its undo();
// `redo` does the reverse. The store owns applying do()/undo() to the state —
// this class only tracks ordering.
// -----------------------------------------------------------------------------
import type { Command } from './commands';

export class History {
  private undoStack: Command[] = [];
  private redoStack: Command[] = [];

  push(cmd: Command): void {
    this.undoStack.push(cmd);
    this.redoStack.length = 0; // a new action discards the redo future
  }

  /** Pop the most recent command for undoing; returns null if nothing to undo. */
  undo(): Command | null {
    const cmd = this.undoStack.pop();
    if (!cmd) return null;
    this.redoStack.push(cmd);
    return cmd;
  }

  /** Pop the most recently undone command for redoing; null if nothing to redo. */
  redo(): Command | null {
    const cmd = this.redoStack.pop();
    if (!cmd) return null;
    this.undoStack.push(cmd);
    return cmd;
  }

  canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undoLabel(): string | null {
    return this.undoStack.at(-1)?.label ?? null;
  }
  redoLabel(): string | null {
    return this.redoStack.at(-1)?.label ?? null;
  }

  clear(): void {
    this.undoStack.length = 0;
    this.redoStack.length = 0;
  }
}
